import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { PageChangeEvent } from '@app/shared/components/pagination/page-change-event';
import { PaginationForm } from '@app/shared/components/pagination/pagination-form';
import { Page } from '@app/shared/models/page';
import { PageDefinition } from '@app/shared/models/page-definition';
import { PreferenceStorageService } from '@app/shared/services/preference-storage.service';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, first } from 'rxjs/operators';
import { FormGroupTyped } from 'src/types/typed-forms';

@UntilDestroy()
@Component({
  selector: 'portal-pagination',
  templateUrl: './pagination.component.html',
  styles: [],
  standalone: false,
})
export class PaginationComponent implements OnChanges, OnInit {
  private defaultSteps: number[] = [10, 20, 50, 100, 200];
  private initialized: boolean = false;

  /** bootstrap button size */
  @Input()
  buttonSize: 'sm' | 'lg' | '' = '';

  @Input()
  disabled: boolean = false;

  @Input()
  initializeFromStorage: boolean = true;

  @Input()
  page?: Page<any>;

  @Input()
  simple: boolean = false;

  @Input()
  selected: number | null = null;

  @Input()
  simpleWithInfo: boolean = false;

  @Input()
  steps: number[] = this.defaultSteps;

  @Input()
  storageId: string = 'pagination';

  @Input()
  hideControls: boolean = false;

  @Output()
  onPageChanged: EventEmitter<PageChangeEvent> = new EventEmitter();

  @ViewChild('p', { static: false })
  popover: NgbPopover;

  @HostBinding('class')
  className: string;

  MAX: number = Math.pow(2, 31) - 1;

  description: string;
  form: FormGroupTyped<PaginationForm>;
  internalPage: Page;
  pages: number;

  constructor(private preferenceStorageService: PreferenceStorageService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized === true) {
      if ('page' in changes) {
        this.internalPage = changes.page.currentValue;

        this.form.patchValue({
          page: this.internalPage.page,
          pageSize: this.internalPage.pageSize,
        });
      }

      this.calculateTotal(this.internalPage);
      this.updateSteps();
    }
  }

  ngOnInit() {
    this.internalPage = { ...this.page };

    this.form = PaginationForm.initialisedFormGroup({
      page: this.internalPage.page,
      pageSize: this.internalPage.pageSize,
    });

    if (this.simple !== false) {
      this.simple = true;
    }

    if (this.simpleWithInfo !== false) {
      this.simpleWithInfo = true;
    }

    this.calculateTotal(this.internalPage);
    this.defaultSteps = this.steps;
    this.updateSteps();

    this.initialized = true;

    if (this.initializeFromStorage) {
      this.preferenceStorageService
        .preference$<number>(this.storageId)
        .pipe(first())
        .subscribe((pageSize) => {
          if (pageSize !== undefined) {
            this.form.patchValue(
              {
                pageSize: pageSize,
              },
              {
                emitEvent: false,
                onlySelf: true,
              }
            );

            this.internalPage.page = 1;
            this.internalPage.pageSize = pageSize;

            this.calculateTotal(this.internalPage);
            this.updateSteps();
            this.emit(true);
          }
        });
    }

    this.form
      .get('page')
      .valueChanges.pipe(debounceTime(400), untilDestroyed(this))
      .subscribe((page) => {
        if (page !== this.internalPage.page) {
          if (typeof page === 'number') {
            this.updatePage(page);

            this.emit();
          }
        }
      });
  }

  emit(fromStorage: boolean = false) {
    const { page, pageSize } = this.internalPage;

    // Emit always in a timeout function so angular can track the changes correctly and
    // does not throw ExpressionHasChangedAfterItHasBeenChecked errors.
    setTimeout(() => {
      this.onPageChanged.emit({
        fromStorage,
        page: Number(page),
        pageSize: Number(pageSize),
      });
    });
  }

  isActive(length: number) {
    return this.internalPage.pageSize === length;
  }

  isHidden(length: number) {
    return this.internalPage.total <= length;
  }

  onPageSizeChanged() {
    const pageSize = Number(this.form.value.pageSize);

    if (pageSize === -1) {
      this.internalPage.pageSize = this.MAX;
    } else {
      this.internalPage.pageSize = pageSize;
    }

    this.internalPage.page = 1;

    this.popover.close();

    this.calculateTotal(this.internalPage);
    this.updateSteps();
    this.emit();

    this.preferenceStorageService.updatePreference(this.storageId, this.internalPage.pageSize, true);
  }

  selectPage(input: PageDefinition) {
    let page: number;

    if (typeof input === 'string') {
      page = this.resolve(input);

      // Update the form value so the number toggle is correct
      this.form.patchValue({
        page,
      });
    } else {
      if (input < 1) {
        page = 1;
      } else if (input > this.internalPage.pages) {
        page = this.internalPage.pages;
      } else {
        page = input;
      }
    }

    this.internalPage.page = page;

    this.emit();
  }

  private calculateTotal(page: Page) {
    const pageSize = page.pageSize;
    const total = page.filtered;

    let classes: string[] = [];

    if (pageSize < total) {
      this.description = 'common.pagination.description.multiple';
      this.internalPage.pages = Math.ceil(total / pageSize);
    } else {
      this.internalPage.pages = 1;

      if (total > 1) {
        this.description = 'common.pagination.description.singlePage';
      } else if (total === 1) {
        this.description = 'common.pagination.description.single';
      } else {
        this.description = 'common.pagination.description.empty';
      }
    }

    if (this.simpleWithInfo === true) {
      if (total === 0) {
        this.description = 'common.pagination.description.noEntries';
      } else if (total === 1) {
        this.description = 'common.pagination.description.single';
      } else {
        this.description = 'common.pagination.description.simple';
      }

      if (page.filtered !== page.total) {
        this.description += '.filtered';
      }
    }

    if (total === 0) {
      classes.push('pagination--empty');
    } else if (this.internalPage.pages === 1) {
      classes.push('pagination--single-page');
    } else {
      classes.push('');
    }

    if (this.simple || this.simpleWithInfo) {
      classes.push('pagination--simple');
    } else {
      classes.push('pagination--default');
    }

    if (this.buttonSize === 'sm') {
      classes.push('pagination--small');
    }

    this.className = classes.join(' ');
  }

  private resolve(page: string): number {
    const def = {
      first: 1,
      last: this.internalPage.pages,
      next: this.internalPage.page + 1,
      previous: this.internalPage.page - 1,
    };

    return def[page] || 1;
  }

  private updatePage(page: number) {
    // Show the last page if the entered page is larger than the number of pages
    if (page > this.internalPage.pages) {
      page = this.internalPage.pages;
    }

    const to = page * this.internalPage.pageSize;

    this.internalPage.page = page;
    this.internalPage.from = (page - 1) * this.internalPage.pageSize + 1;
    this.internalPage.to = to > this.internalPage.total ? this.internalPage.total : to;
  }

  private updateSteps() {
    const totalEntries = this.internalPage.filtered;
    this.steps = this.defaultSteps.filter((step) => step < totalEntries);
  }
}
